import React from "react";
import { Grid, Autocomplete, TextField, Tooltip, Box } from "@mui/material";
import { useFormikContext } from "formik";
import { VehicleTextField, VehicleCheckboxField } from "../VehicleFormFields";
import { VehicleFormValues } from "../../../validation/vehicleValidationSchema";
import { US_STATES } from "../../../../../constants/usStates";

// State Dropdown Component
interface StateDropdownProps {
  name: string;
  label: string;
  placeholder?: string;
  tooltip?: string;
}

const StateDropdown: React.FC<StateDropdownProps> = ({
  name,
  label,
  placeholder,
  tooltip,
}) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<VehicleFormValues>();

  const fieldValue = (values as any)[name] || "";
  const fieldError = (errors as any)[name];
  const fieldTouched = (touched as any)[name];

  const handleChange = (_: React.SyntheticEvent, newValue: string | null) => {
    setFieldValue(name, newValue || "");
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
  };

  const stateDropdown = (
    <Autocomplete
      fullWidth
      size="small"
      options={US_STATES}
      getOptionLabel={(option) => `${option.code} - ${option.name}`}
      isOptionEqualToValue={(option, value) => option.code === value?.code}
      value={US_STATES.find(state => state.code === fieldValue) || null}
      onChange={(_, newValue) => handleChange(_, newValue?.code || null)}
      onBlur={handleBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={fieldTouched && Boolean(fieldError)}
          helperText={fieldTouched && fieldError}
        />
      )}
      sx={{ mb: 2 }}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      <Box>{stateDropdown}</Box>
    </Tooltip>
  ) : (
    stateDropdown
  );
};

const TruckInfoTab: React.FC = () => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <VehicleTextField 
          name="color" 
          label="Color" 
          placeholder="e.g., White, Blue"
        />
      </Grid>
      <Grid item xs={12} sm={8}>
        <VehicleTextField 
          name="licensePlate" 
          label="License Plate" 
          placeholder="e.g., ABC123"
          tooltip="Current license plate number"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <StateDropdown 
          name="licenseState" 
          label="License State" 
          placeholder="e.g., IL, CA, TX"
          tooltip="State where the license plate is registered"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleTextField 
          name="fuelCardNumber" 
          label="Fuel Card Number" 
          tooltip="Fuel card assigned to this vehicle"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleTextField 
          name="fuelType" 
          label="Fuel Type" 
          placeholder="e.g., Diesel, Gas"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleTextField 
          name="iPassNumber" 
          label="I-Pass Number" 
          tooltip="I-Pass transponder number for tolls"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleTextField 
          name="bestPassNumber" 
          label="Best Pass Number" 
          tooltip="Best Pass transponder number"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleTextField 
          name="prePassEzPassNumber" 
          label="Pre Pass Ez Pass Number" 
          tooltip="PrePass or EZ-Pass transponder number"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleTextField 
          name="tireSize" 
          label="Tire Size" 
          placeholder="e.g., 295/75R22.5"
          tooltip="Standard tire size for this vehicle"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleCheckboxField 
          name="deductTolls" 
          label="Deduct Tolls" 
          tooltip="Whether tolls should be deducted from driver pay"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <VehicleCheckboxField 
          name="deductFuel" 
          label="Deduct Fuel" 
          tooltip="Whether fuel costs should be deducted from driver pay"
        />
      </Grid>
    </Grid>
  );
};

export default TruckInfoTab;
